'use client';
/**
 * CORRIGER UN ACTE DU PLAN (spec 002, récit 4, R7, EF-014 — 04/11/2026).
 *
 * Un acte du plan ne se SUPPRIME plus (la route est retirée) : il se corrige,
 * par son auteur ou par le RQ, avec un motif ; l'état antérieur des rubriques
 * corrigées est gardé dans l'acte (`corrections`) et la correction part au
 * journal de l'appareil (`MaintenanceActeCorrige`). Même place que la correction
 * d'une intervention (`CorrigerIntervention`) : dans la fenêtre de la
 * maintenance, sous l'en-tête — un seul chemin d'écriture par objet.
 *
 * Rubriques : date de réalisation, opérations (faite / non faite, motif),
 * commentaire, anomalie, NC liée — ou, pour une échéance close « non
 * réalisée », son motif.
 */
import React, { useState } from 'react';
import { useGed } from '@/lib/store';
import { aujourdhuiIso, fmtDate } from '@/lib/meta';
import { I, inputStyle, Selecteur, useBusy } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { lbl } from '@/components/screens/ressources/commun';
import type { EntreeHistorique } from '@/lib/historique-maintenance';

const MOTIF_MIN = 10;

export function CorrigerActe({ e, onFerme }: { e: EntreeHistorique; onFerme: () => void }) {
  const { smqData, ncs } = useGed();
  const { busy, run } = useBusy();
  const nonRealisee = e.issue === 'non_realisee';
  /* Les opérations PRÉVUES par la ligne, avec ce que l'acte en dit. */
  const prevues = e.planOperations.length ? e.planOperations : e.operations.map((o) => o.operation);
  const [etats, setEtats] = useState<Record<string, 'fait' | 'non'>>(() => Object.fromEntries(
    prevues.map((o) => [o, e.operations.find((x) => x.operation === o)?.fait === false ? 'non' : 'fait']),
  ));
  const [motifsOps, setMotifsOps] = useState<Record<string, string>>(() => Object.fromEntries(
    e.operations.filter((o) => !o.fait && o.motif).map((o) => [o.operation, o.motif as string]),
  ));
  const [dateFaite, setDateFaite] = useState(e.date);
  const [commentaire, setCommentaire] = useState(e.commentaire);
  const [anomalie, setAnomalie] = useState(e.probleme);
  const [ncRef, setNcRef] = useState(e.ncRef);
  const [motifNonRealisee, setMotifNonRealisee] = useState(nonRealisee ? e.commentaire : '');
  const [motif, setMotif] = useState('');

  const nonFaites = prevues.filter((o) => etats[o] === 'non');
  const sansMotifOp = nonFaites.filter((o) => !(motifsOps[o] || '').trim()).length;
  const bloque = motif.trim().length < MOTIF_MIN ? `Motif de la correction obligatoire (${MOTIF_MIN} caractères au minimum).`
    : nonRealisee && motifNonRealisee.trim().length < MOTIF_MIN ? `Le motif de non-réalisation garde ${MOTIF_MIN} caractères au minimum.`
      : !nonRealisee && !dateFaite ? 'La date de réalisation est obligatoire.'
        : !nonRealisee && sansMotifOp && !e.reprise ? `${sansMotifOp} opération(s) non faite(s) sans motif.` : '';

  const enregistrer = () => run('corriger', async () => {
    const corps: Record<string, unknown> = { motif: motif.trim() };
    if (nonRealisee) {
      corps.motifNonRealisee = motifNonRealisee.trim();
    } else {
      corps.dateFaite = dateFaite;
      corps.commentaire = commentaire.trim();
      corps.anomalie = anomalie;
      corps.ncRef = ncRef;
      if (prevues.length) {
        corps.operations = prevues.filter((o) => etats[o] === 'fait');
        corps.motifs = Object.fromEntries(nonFaites.map((o) => [o, (motifsOps[o] || '').trim()]));
      }
    }
    const r = await smqData<{ inchange?: boolean }>(`ged/maintenances/actes/${e.acteId}/corriger`, corps,
      'Acte corrigé — l’état antérieur est gardé, la correction part au journal de l’appareil.');
    if (r) onFerme();
  });

  return (
    <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '12px 0', display: 'grid', gap: 12, fontSize: 12.5 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ color: 'var(--text-strong)' }}>
          Corriger l’acte de l’échéance du {fmtDate(e.datePrevue) || e.datePrevue || '—'}
        </strong>
        <span style={{ color: 'var(--text-muted)' }}>
          On ne supprime pas un acte : la correction se motive, l’état antérieur est gardé et la correction part au journal.
        </span>
      </div>

      {nonRealisee ? (
        <div>
          <label style={lbl} htmlFor={`ca-nr-${e.acteId}`}>Pourquoi n’a-t-elle pas été faite ?</label>
          <textarea id={`ca-nr-${e.acteId}`} value={motifNonRealisee} onChange={(ev) => setMotifNonRealisee(ev.target.value)}
            rows={2} maxLength={2000} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
      ) : (
        <>
          <div>
            <label style={lbl} htmlFor={`ca-date-${e.acteId}`}>Réalisée le</label>
            <input id={`ca-date-${e.acteId}`} type="date" value={dateFaite} max={aujourdhuiIso()}
              onChange={(ev) => setDateFaite(ev.target.value)} style={{ ...inputStyle, maxWidth: 200 }} />
          </div>
          {prevues.length > 0 && (
            <div>
              <span style={lbl}>Opérations</span>
              <ol style={{ display: 'grid', gap: 6, margin: 0, paddingInlineStart: 22 }}>
                {prevues.map((o) => (
                  <li key={o}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <span style={{ flex: '1 1 240px', color: etats[o] === 'non' ? 'var(--red-600)' : 'inherit' }}>{o}</span>
                      <span role="radiogroup" aria-label={o} style={{ display: 'flex', gap: 12, whiteSpace: 'nowrap' }}>
                        <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
                          <input type="radio" name={`ca-op-${e.acteId}-${o}`} checked={etats[o] === 'fait'} onChange={() => setEtats((x) => ({ ...x, [o]: 'fait' }))} />faite
                        </label>
                        <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
                          <input type="radio" name={`ca-op-${e.acteId}-${o}`} checked={etats[o] === 'non'} onChange={() => setEtats((x) => ({ ...x, [o]: 'non' }))} />non faite
                        </label>
                      </span>
                    </div>
                    {etats[o] === 'non' && (
                      <input value={motifsOps[o] || ''} onChange={(ev) => setMotifsOps((m) => ({ ...m, [o]: ev.target.value }))}
                        aria-label={`Pourquoi « ${o} » n’a pas été faite`} maxLength={500}
                        placeholder="Pourquoi n’a-t-elle pas été faite ?" style={{ ...inputStyle, marginTop: 4 }} />
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}
          <div>
            <label style={lbl} htmlFor={`ca-com-${e.acteId}`}>Commentaire</label>
            <textarea id={`ca-com-${e.acteId}`} value={commentaire} onChange={(ev) => setCommentaire(ev.target.value)} rows={2}
              style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type="checkbox" checked={anomalie} onChange={(ev) => setAnomalie(ev.target.checked)} style={{ marginTop: 2 }} />
            <span>Cette maintenance a révélé une anomalie.</span>
          </label>
          <div>
            <label style={lbl} htmlFor={`ca-nc-${e.acteId}`}>Non-conformité liée</label>
            <Selecteur id={`ca-nc-${e.acteId}`} value={ncRef} onChange={setNcRef} style={inputStyle}>
              <option value="">— aucune —</option>
              {ncRef && !ncs.some((n) => n.ref === ncRef) && <option value={ncRef}>{ncRef}</option>}
              {ncs.filter((n) => n.equipementCode === e.equipementCode || n.ref === ncRef)
                .map((n) => <option key={n.ref} value={n.ref}>{`${n.ref} · ${n.titre}`}</option>)}
            </Selecteur>
          </div>
        </>
      )}

      <div>
        <label style={lbl} htmlFor={`ca-motif-${e.acteId}`}>Motif de la correction *</label>
        <input id={`ca-motif-${e.acteId}`} value={motif} onChange={(ev) => setMotif(ev.target.value)} maxLength={2000}
          placeholder="Ex. date mal saisie : faite le 03 et non le 04 (bon d’intervention joint)." style={inputStyle} />
      </div>
      {bloque && <p style={{ margin: 0, color: 'var(--amber-600)' }}>{bloque}</p>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Bouton taille="sm" onClick={onFerme}>Revenir à la fiche</Bouton>
        <Bouton variante="primaire" taille="sm" onClick={enregistrer} disabled={busy === 'corriger' || !!bloque} title={bloque || undefined}>
          <I n="check" s={13} />Enregistrer la correction
        </Bouton>
      </div>
    </div>
  );
}
